import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../SupabaseClient.tsx";
import "./ReceiptDetail.css";
import Order, { Attachment } from "../types/Order.ts";
import EditReceiptForm from "../components/EditReceiptForm.tsx";
import ActionButton from "../components/ActionButton.tsx";
import ReadOnlyBanner from "../components/ReadOnlyBanner.tsx";
import { Lapse } from "../utils/checkout.ts";
import { PiArrowLeft, PiCaretLeft, PiCaretRight } from "react-icons/pi";

type SortKey = "name" | "purchase_date" | "status" | "beneficiary" | "amount";
type SortDirection = "asc" | "desc";

interface ReceiptDetailProps {
  // Set when the account is read-only because a trial or subscription has ended
  lapse?: Lapse | null;
}

const ReceiptDetail = ({ lapse = null }: ReceiptDetailProps) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [order, setOrder] = useState<Order | null>(
    location.state?.order || null
  );
  const [receiptUrl, setReceiptUrl] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [allOrders, setAllOrders] = useState<Order[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(-1);
  const lastFetchedId = useRef<string | null>(null);

  // Get sort preferences from session storage
  const getSortPreferences = () => {
    const sortKey =
      (sessionStorage.getItem("receiptSortKey") as SortKey) || "purchase_date";
    const sortDirection =
      (sessionStorage.getItem("receiptSortDirection") as SortDirection) ||
      "desc";
    const showDraftsOnly = sessionStorage.getItem("showDraftsOnly") === "true";
    return { sortKey, sortDirection, showDraftsOnly };
  };

  // This effect handles fetching the current order based on ID
  useEffect(() => {
    // Reset receipt URL when ID changes
    setReceiptUrl(null);
    setAttachments([]);

    // Skip if we already fetched this ID or if order already matches
    if (lastFetchedId.current === id) {
      if (order?.attachments && order.attachments.length > 0) {
        const primaryAttachment =
          order.attachments.find((a) => a.is_primary) || order.attachments[0];
        fetchReceiptUrl(primaryAttachment.storage_path);
        setAttachments(order.attachments);
      }
      return;
    }

    // If we don't have the full order details (from navigation) or ID changed, fetch it
    if ((!order || order.id !== id) && id) {
      lastFetchedId.current = id ?? null;
      const fetchOrder = async () => {
        // Fetch order data
        const { data, error } = await supabase
          .from("orders")
          .select(
            "id, purchase_date, amount, status(name), origin(name), name, notes, beneficiary, merchant, category, purchase_id, created_at"
          )
          .eq("id", id)
          .single();

        if (error) {
          console.error("Error fetching order:", error);
          return;
        }

        if (data) {
          // Fetch attachments for this order
          const { data: attachmentsData, error: attachmentsError } =
            await supabase
              .from("order_attachments")
              .select(
                "id, order_id, storage_path, original_filename, mime_type, is_primary, created_at"
              )
              .eq("order_id", id)
              .order("is_primary", { ascending: false });

          if (attachmentsError) {
            console.error("Error fetching attachments:", attachmentsError);
          }

          // Transform the data to match the Order type
          const transformedOrder: Order = {
            ...data,
            status: Array.isArray(data.status) ? data.status[0] : data.status,
            origin: Array.isArray(data.origin) ? data.origin[0] : data.origin,
            attachments: attachmentsData || [],
          };
          setOrder(transformedOrder);
          setAttachments(attachmentsData || []);

          // Use primary attachment if available
          if (attachmentsData && attachmentsData.length > 0) {
            const primaryAttachment =
              attachmentsData.find((a: Attachment) => a.is_primary) ||
              attachmentsData[0];
            fetchReceiptUrl(primaryAttachment.storage_path);
          }
        }
      };

      fetchOrder();
    } else if (order && order.id === id) {
      // If we already have this order but need the receipt URL
      lastFetchedId.current = id ?? null;
      if (order.attachments && order.attachments.length > 0) {
        const primaryAttachment =
          order.attachments.find((a) => a.is_primary) || order.attachments[0];
        fetchReceiptUrl(primaryAttachment.storage_path);
        setAttachments(order.attachments);
      }
    }
  }, [id, order]);

  // Fetch all orders to enable navigation
  useEffect(() => {
    const fetchAllOrders = async () => {
      const { sortKey, sortDirection, showDraftsOnly } = getSortPreferences();

      const { data, error } = await supabase
        .from("orders")
        .select(
          "id, purchase_date, amount, status(name), origin(name), name, beneficiary, created_at"
        );

      if (error) {
        console.error("Error fetching all orders:", error);
        return;
      }

      if (!data) return;

      // Map status and origin from arrays to single objects
      let orders = data.map((order: any) => ({
        ...order,
        status: Array.isArray(order.status) ? order.status[0] : order.status,
        origin: Array.isArray(order.origin) ? order.origin[0] : order.origin,
      }));

      // Filter by draft status if needed
      if (showDraftsOnly) {
        orders = orders.filter((order) => order.status?.name === "Draft");
      }

      // Sort orders according to session storage preferences
      orders.sort((a, b) => {
        let aValue: any, bValue: any;
        switch (sortKey) {
          case "name":
            aValue = a.name || "";
            bValue = b.name || "";
            break;
          case "purchase_date":
            aValue = new Date(a.purchase_date).getTime();
            bValue = new Date(b.purchase_date).getTime();
            break;
          case "status":
            aValue = a.status?.name || "";
            bValue = b.status?.name || "";
            break;
          case "beneficiary":
            aValue = a.beneficiary || "";
            bValue = b.beneficiary || "";
            break;
          case "amount":
            aValue = a.amount || 0;
            bValue = b.amount || 0;
            break;
          default:
            aValue = "";
            bValue = "";
        }

        // Primary sort by the selected column
        if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
        if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;

        // Secondary sort by ID to ensure consistent order when primary values are equal
        return a.id < b.id ? -1 : 1;
      });

      setAllOrders(orders);

      // Find current order index
      if (id) {
        const index = orders.findIndex((order) => order.id === id);
        setCurrentIndex(index);
      }
    };

    fetchAllOrders();
  }, [id]);

  const fetchReceiptUrl = async (storagePath: string) => {
    const { data, error } = await supabase.storage
      .from("order_receipts")
      .createSignedUrl(storagePath, 60);

    if (error) {
      console.error("Error fetching receipt URL:", error);
    } else {
      setReceiptUrl(data.signedUrl);
    }
  };

  const handleBack = () => {
    navigate("/receipt-library");
  };

  // Handle attachment selection to switch preview
  const handleAttachmentSelect = async (attachment: Attachment) => {
    fetchReceiptUrl(attachment.storage_path);
  };

  // Handle attachments change to refresh the list
  const handleAttachmentsChange = async () => {
    if (!id) return;

    const { data: attachmentsData, error: attachmentsError } = await supabase
      .from("order_attachments")
      .select(
        "id, order_id, storage_path, original_filename, mime_type, is_primary, created_at"
      )
      .eq("order_id", id)
      .order("is_primary", { ascending: false });

    if (attachmentsError) {
      console.error("Error fetching attachments:", attachmentsError);
      return;
    }

    setAttachments(attachmentsData || []);

    // If attachments exist, load the primary one's URL
    if (attachmentsData && attachmentsData.length > 0) {
      const primaryAttachment =
        attachmentsData.find((a: Attachment) => a.is_primary) ||
        attachmentsData[0];
      fetchReceiptUrl(primaryAttachment.storage_path);
    } else {
      setReceiptUrl(null);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      const prevOrder = allOrders[currentIndex - 1];
      // Instead of clearing and navigating, just update the URL and state directly
      navigate(`/receipt-library/${prevOrder.id}`, { replace: true });

      // Update the ID in URL without reloading the component
      const fetchFullOrder = async (orderId: string) => {
        const { data, error } = await supabase
          .from("orders")
          .select(
            "id, purchase_date, amount, status(name), origin(name), name, notes, beneficiary, merchant, category, purchase_id, created_at"
          )
          .eq("id", orderId)
          .single();

        if (error) {
          console.error("Error fetching order:", error);
          return;
        }

        if (data) {
          // Fetch attachments for this order
          const { data: attachmentsData, error: attachmentsError } =
            await supabase
              .from("order_attachments")
              .select(
                "id, order_id, storage_path, original_filename, mime_type, is_primary, created_at"
              )
              .eq("order_id", orderId)
              .order("is_primary", { ascending: false });

          if (attachmentsError) {
            console.error("Error fetching attachments:", attachmentsError);
          }

          // Transform the data to match the Order type
          const transformedOrder: Order = {
            ...data,
            status: Array.isArray(data.status) ? data.status[0] : data.status,
            origin: Array.isArray(data.origin) ? data.origin[0] : data.origin,
            attachments: attachmentsData || [],
          };
          setOrder(transformedOrder);
          setCurrentIndex(currentIndex - 1);
          setAttachments(attachmentsData || []);

          // Use primary attachment if available
          if (attachmentsData && attachmentsData.length > 0) {
            const primaryAttachment =
              attachmentsData.find((a: Attachment) => a.is_primary) ||
              attachmentsData[0];
            fetchReceiptUrl(primaryAttachment.storage_path);
          } else {
            setReceiptUrl(null);
          }
        }
      };

      fetchFullOrder(prevOrder.id);
    }
  };

  const handleNext = () => {
    if (currentIndex < allOrders.length - 1) {
      const nextOrder = allOrders[currentIndex + 1];
      // Instead of clearing and navigating, just update the URL and state directly
      navigate(`/receipt-library/${nextOrder.id}`, { replace: true });

      // Update the ID in URL without reloading the component
      const fetchFullOrder = async (orderId: string) => {
        const { data, error } = await supabase
          .from("orders")
          .select(
            "id, purchase_date, amount, status(name), origin(name), name, notes, beneficiary, merchant, category, purchase_id, created_at"
          )
          .eq("id", orderId)
          .single();

        if (error) {
          console.error("Error fetching order:", error);
          return;
        }

        if (data) {
          // Fetch attachments for this order
          const { data: attachmentsData, error: attachmentsError } =
            await supabase
              .from("order_attachments")
              .select(
                "id, order_id, storage_path, original_filename, mime_type, is_primary, created_at"
              )
              .eq("order_id", orderId)
              .order("is_primary", { ascending: false });

          if (attachmentsError) {
            console.error("Error fetching attachments:", attachmentsError);
          }

          // Transform the data to match the Order type
          const transformedOrder: Order = {
            ...data,
            status: Array.isArray(data.status) ? data.status[0] : data.status,
            origin: Array.isArray(data.origin) ? data.origin[0] : data.origin,
            attachments: attachmentsData || [],
          };
          setOrder(transformedOrder);
          setCurrentIndex(currentIndex + 1);
          setAttachments(attachmentsData || []);

          // Use primary attachment if available
          if (attachmentsData && attachmentsData.length > 0) {
            const primaryAttachment =
              attachmentsData.find((a: Attachment) => a.is_primary) ||
              attachmentsData[0];
            fetchReceiptUrl(primaryAttachment.storage_path);
          } else {
            setReceiptUrl(null);
          }
        }
      };

      fetchFullOrder(nextOrder.id);
    }
  };

  if (!order) {
    return <div>Loading...</div>;
  }

  return (
    <div className="global-page-screen">
      <div className="page">
        <div className="page-header">
          <div className="page-header-title">
            <h1>Receipt Detail</h1>
            <p>
              {lapse
                ? "View your receipt."
                : "Let's manage and edit your receipt."}
            </p>
          </div>
          <ActionButton
            icon={<PiArrowLeft />}
            text="Back to Library"
            onClick={handleBack}
            secondary={true}
            responsive={true}
          />
        </div>
        {lapse && <ReadOnlyBanner lapse={lapse} />}
        <EditReceiptForm
          key={order.id} // Add key prop to force re-render when order changes
          order={order}
          readOnly={Boolean(lapse)}
          receiptUrl={receiptUrl}
          fileName={
            attachments.length > 0
              ? (attachments.find((a) => a.is_primary) || attachments[0])
                .original_filename
              : undefined
          }
          attachments={attachments}
          detailView={true}
          onAttachmentSelect={handleAttachmentSelect}
          onAttachmentsChange={handleAttachmentsChange}
        />

        <div className="receipt-navigation">
          <ActionButton
            icon={<PiCaretLeft />}
            text="Previous Receipt"
            onClick={handlePrevious}
            secondary={true}
            disabled={currentIndex <= 0}
            responsive={true}
          />
          <div className="receipt-position">
            {currentIndex >= 0
              ? `${currentIndex + 1} of ${allOrders.length}`
              : ""}
          </div>
          <ActionButton
            icon={<PiCaretRight />}
            text="Next Receipt"
            onClick={handleNext}
            secondary={true}
            disabled={currentIndex >= allOrders.length - 1}
            iconPosition="right"
            responsive={true}
          />
        </div>
      </div>
    </div>
  );
};

export default ReceiptDetail;
